import React, { useState, useContext } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, CalendarDays, Star, Link, Check, CheckCircle2 } from "lucide-react";
import { format, parseISO, isBefore, startOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Badge } from "../../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import { getPublicFileUrl } from "../../config";

const TZ_BR = "America/Sao_Paulo";
const formatBrDate = (iso, withYear = true) => {
  if (!iso) return "";
  try {
    const d = new Date(String(iso));
    if (Number.isNaN(d.getTime())) return String(iso);
    const fmt = d.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      ...(withYear ? { year: "numeric" } : {}),
      timeZone: TZ_BR,
    });
    return fmt;
  } catch {
    return String(iso);
  }
};
const formatBrDateTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(String(iso));
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
      timeZone: TZ_BR,
    });
  } catch {
    return String(iso);
  }
};

const hexToRgba = (hex, alpha) => {
  if (!hex) return `rgba(203, 213, 225, ${alpha})`;
  const h = hex.replace("#", "");
  if (h.length !== 6) return `rgba(203, 213, 225, ${alpha})`;
  return `rgba(${parseInt(h.slice(0, 2), 16)},${parseInt(h.slice(2, 4), 16)},${parseInt(h.slice(4, 6), 16)},${alpha})`;
};

const initialsOf = (name) => {
  const s = (name || "").trim().split(/\s+/).filter(Boolean);
  if (s.length === 0) return "?";
  if (s.length === 1) return s[0].slice(0, 2).toUpperCase();
  return (s[0][0] + s[s.length - 1][0]).toUpperCase();
};

const simpleHash = (s) => {
  let h = 2166136261;
  const str = String(s || "?");
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0);
};

const colorForName = (s) => {
  const palette = [
    "#ef4444", "#f97316", "#f59e0b", "#eab308", "#84cc16",
    "#22c55e", "#10b981", "#14b8a6", "#06b6d4", "#0ea5e9",
    "#3b82f6", "#6366f1", "#8b5cf6", "#a855f7", "#d946ef",
    "#ec4899", "#f43f5e",
  ];
  return palette[simpleHash(s) % palette.length];
};

const identiconSvgUrl = (name) => {
  const n = String(name || "?").trim() || "?";
  const bg = colorForName(n);
  const text = initialsOf(n);
  const w = 80;
  const h = 80;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">` +
    `<rect width="${w}" height="${h}" fill="${bg}"/>` +
    `<text x="50%" y="52%" dominant-baseline="middle" text-anchor="middle" ` +
    `fill="#ffffff" font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" ` +
    `font-weight="700" font-size="30">${text}</text></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const avatarSrcOf = (u) => {
  if (!u) return undefined;
  const raw = u.profilePicUrl || u.avatarUrl || u.avatar || u.picUrl || undefined;
  if (!raw) return identiconSvgUrl(u?.name || `user-${u?.id || "?"}`);
  if (typeof raw !== "string") return identiconSvgUrl(u?.name || `user-${u?.id || "?"}`);
  const resolved = getPublicFileUrl(raw) || raw;
  if (!resolved) return identiconSvgUrl(u?.name || `user-${u?.id || "?"}`);
  return resolved;
};

const dbToStars = (p) => {
  switch (Number(p)) {
    case 1:
      return 2;
    case 2:
      return 4;
    case 3:
      return 5;
    default:
      return 0;
  }
};

function Estrelas({ quantidade, tamanho = 12 }) {
  const n = Math.max(0, Math.min(5, Number(quantidade) || 0));
  return (
    <span className="inline-flex items-center leading-none" aria-label={`Prioridade ${n} de 5`}>
      {[0, 1, 2, 3, 4].map((i) => {
        const filled = i < n;
        return (
          <Star
            key={i}
            style={{ width: tamanho, height: tamanho }}
            className={cn(
              "shrink-0 -mx-0.5",
              filled
                ? "fill-amber-400 text-amber-400"
                : "fill-transparent text-slate-200"
            )}
          />
        );
      })}
    </span>
  );
}

function ItemCard({ item, onEditItem }) {
  const { user: authUser } = useContext(AuthContext);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `item:${item.id}`,
  });
  const [copiedLink, setCopiedLink] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const estrelas = dbToStars(item.priority);

  const dataStr = String(item.dueDate || "").slice(0, 10);
  const dataVazia = !dataStr;
  const dataDate = dataVazia ? null : parseISO(dataStr);
  const vencida = dataDate && isBefore(dataDate, startOfDay(new Date()));

  const status = item.status;
  const statusCor = status?.color || null;
  const statusNome = status?.name || null;
  const ownerCandidate = item.user && item.user.id ? item.user : (item.userId ? { id: item.userId, name: "Responsável" } : null);
  const effectiveAssignee = (item.assignee && item.assignee.id)
    ? item.assignee
    : (ownerCandidate
        ? ownerCandidate
        : (authUser?.id ? authUser : null));
  const assignee = effectiveAssignee;
  const effectiveAssigneeId = assignee ? String(assignee.id) : "";
  const sharesUsers = Array.isArray(item.shares) ? item.shares
    .filter(s => s && s?.user?.id && String(s.user.id) !== effectiveAssigneeId)
    .map(s => s.user) : [];

  const handleCopiarLink = async (e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/tarefas/item/${encodeURIComponent(item.id)}`;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopiedLink(true);
      toast.success("Link da tarefa copiado");
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      console.error(err);
      toast.error("Não foi possível copiar o link");
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="mb-2 flex cursor-grab flex-col gap-1.5 rounded-md border border-border bg-card p-3 shadow-[0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-none active:cursor-grabbing relative"
      onClick={(e) => {
        if (e.target.closest("[data-drag-handle]")) return;
        onEditItem?.(item);
      }}
    >
      {statusCor && (
        <div className="absolute top-2 right-2 inline-flex items-center gap-1">
          <span
            className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold text-white shadow-sm border border-black/10"
            style={{ backgroundColor: statusCor }}
            title={statusNome || ""}
          >
            {statusNome || "Status"}
          </span>
          <Button
            size="sm"
            variant="ghost"
            className="h-5 w-5 p-0 text-muted-foreground hover:text-indigo-600 dark:hover:text-indigo-400 bg-card/80 dark:bg-slate-900/80 rounded shadow-sm border border-border"
            onClick={handleCopiarLink}
            aria-label="Copiar link da tarefa"
            title="Copiar link da tarefa"
          >
            {copiedLink ? (
              <Check className="h-3 w-3 text-emerald-600" />
            ) : (
              <Link className="h-3 w-3" />
            )}
          </Button>
        </div>
      )}

      <div className="flex items-center gap-2 pr-20">
        <span
          data-drag-handle
          {...attributes}
          {...listeners}
          className="opacity-70 text-muted-foreground cursor-grab active:cursor-grabbing shrink-0 -ml-1 p-0.5 rounded hover:bg-muted hover:opacity-100"
        >
          <GripVertical className="h-3.5 w-3.5" />
        </span>
        {estrelas > 0 && <Estrelas quantidade={estrelas} />}
      </div>

      <div className="text-sm font-medium leading-snug text-foreground pr-16">
        {item.title}
      </div>

      {item.description && (
        <div className="line-clamp-1 text-xs text-muted-foreground">{item.description}</div>
      )}

      {Array.isArray(item.tags) && item.tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {item.tags.map((t) => (
            <Badge
              key={t.id}
              variant="outline"
              className="border border-border text-[10px] text-muted-foreground py-0 px-1.5 bg-muted"
              style={{
                borderColor: hexToRgba(t.color, 0.35),
                color: t.color,
              }}
            >
              {t.name}
            </Badge>
          ))}
        </div>
      )}

      {item.completedAt && (
        <div
          className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-400 mt-0.5"
          title={`Concluída em ${formatBrDateTime(item.completedAt)}`}
        >
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 dark:text-emerald-400" />
          Concluída em {formatBrDate(item.completedAt, true)}
        </div>
      )}

      <div className="flex items-end justify-between gap-2 mt-1">
        {dataDate ? (
          <div
            className={cn(
              "flex items-center gap-1 text-[11px]",
              vencida ? "text-red-600 dark:text-red-400 font-semibold" : "text-muted-foreground"
            )}
          >
            <CalendarDays className="h-3 w-3" />
            {format(dataDate, "dd/MMM", { locale: ptBR })}
          </div>
        ) : <span />}

        {(assignee || sharesUsers.length > 0) && (
          <div className="flex -space-x-1.5">
            {assignee && (
              <Avatar key={`assignee-${item.id}`} className="h-5 w-5 shrink-0 border-2 border-background shadow-sm z-10" title={assignee.name}>
                <AvatarImage
                  src={avatarSrcOf(assignee)}
                  alt={assignee.name}
                />
                <AvatarFallback className="text-[8px] font-semibold bg-muted text-muted-foreground">
                  {initialsOf(assignee.name)}
                </AvatarFallback>
              </Avatar>
            )}
            {sharesUsers.slice(0, 3).map((u, i) => (
              <Avatar key={`share-${item.id}-${u.id}-${i}`} className="shrink-0 border-2 border-background shadow-sm" title={u.name} style={{ width: "18px", height: "18px" }}>
                <AvatarImage
                  src={avatarSrcOf(u)}
                  alt={u.name}
                />
                <AvatarFallback className="text-[8px] font-semibold bg-indigo-500/10 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400">
                  {initialsOf(u.name)}
                </AvatarFallback>
              </Avatar>
            ))}
            {sharesUsers.length > 3 && (
              <Avatar className="shrink-0 border-2 border-background bg-muted" style={{ width: "18px", height: "18px" }}>
                <AvatarFallback className="text-[8px] font-semibold text-muted-foreground">
                  +{sharesUsers.length - 3}
                </AvatarFallback>
              </Avatar>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ItemCard;
